import React from 'react';

/**
 * North Indian (Diamond) style Vedic Kundli — SVG only, no canvas.
 *
 * Layout: 12 cells (4 inner diamond quarters + 8 outer triangles), arranged
 * counter-clockwise from the TOP cell which is always House 1 (Lagna).
 *
 * Renders the SIGN-INDEX at each cell (traditional convention — house numbers
 * are NOT written in the North Indian style; only sign numbers).
 *
 * Planet glyphs auto-fit horizontally inside the cell using compact wrapping
 * to avoid overflow into adjacent cells when many planets land together.
 *
 * Props:
 *   - size: square px size (default 340)
 *   - lagnaSign: 0-11 (sign-index of Lagna)
 *   - houseSigns: array[12] — sign at each house (0=Aries) (optional; auto-derived)
 *   - planets: { Sun: { sign, deg, retro, combust, exalted, debilitated, vargottama }, ... }
 *   - title: small label shown above
 *   - onClickPlanet, onClickHouse, onClickLagna
 *   - filteredPlanets: Set of planet names to show (if undefined → show all)
 *   - showDegrees: boolean (default false; D1 typically yes)
 *   - chartId: unique id used in data-testid (e.g. 'D1', 'D9', 'transit')
 */

const PLANET_GLYPH = {
  Sun: 'Su', Moon: 'Mo', Mars: 'Ma', Mercury: 'Me',
  Jupiter: 'Ju', Venus: 'Ve', Saturn: 'Sa', Rahu: 'Ra', Ketu: 'Ke',
};
const PLANET_COLOR = {
  Sun: '#fbbf24', Moon: '#e5e7eb', Mars: '#f87171', Mercury: '#86efac',
  Jupiter: '#fcd34d', Venus: '#f9a8d4', Saturn: '#94a3b8',
  Rahu: '#a78bfa', Ketu: '#f472b6',
};

/**
 * The 12 house cells as POLYGONS in a 1×1 unit square. We render planets at
 * the centroid of each polygon. The polygons map directly to the standard
 * North Indian visual convention (counter-clockwise from the top diamond).
 *
 * Coords use (x, y) where x→right, y→down, both in [0,1].
 */
const HOUSE_POLY = {
  // Inner diamond quarters (centered on each midpoint of the outer square)
  1:  [[0.5, 0.0],  [0.75, 0.25], [0.5, 0.5],  [0.25, 0.25]],     // top diamond
  4:  [[0.0, 0.5],  [0.25, 0.25], [0.5, 0.5],  [0.25, 0.75]],     // left diamond
  7:  [[0.5, 1.0],  [0.25, 0.75], [0.5, 0.5],  [0.75, 0.75]],     // bottom diamond
  10: [[1.0, 0.5],  [0.75, 0.75], [0.5, 0.5],  [0.75, 0.25]],     // right diamond

  // Outer triangles (corner pieces split by one outer diagonal each)
  // Top-left corner → houses 2 (upper) and 3 (lower)
  2:  [[0.0, 0.0],  [0.5, 0.0],   [0.25, 0.25]],                   // top-left upper triangle
  3:  [[0.0, 0.0],  [0.25, 0.25], [0.0, 0.5]],                     // top-left lower triangle
  // Bottom-left corner → houses 5 (upper) and 6 (lower)
  5:  [[0.0, 0.5],  [0.25, 0.75], [0.0, 1.0]],                     // bottom-left upper
  6:  [[0.0, 1.0],  [0.25, 0.75], [0.5, 1.0]],                     // bottom-left lower
  // Bottom-right corner → houses 8 (lower) and 9 (upper)
  8:  [[0.5, 1.0],  [0.75, 0.75], [1.0, 1.0]],                     // bottom-right lower
  9:  [[1.0, 1.0],  [0.75, 0.75], [1.0, 0.5]],                     // bottom-right upper
  // Top-right corner → houses 11 (lower) and 12 (upper)
  11: [[1.0, 0.5],  [0.75, 0.25], [1.0, 0.0]],                     // top-right lower
  12: [[1.0, 0.0],  [0.75, 0.25], [0.5, 0.0]],                     // top-right upper
};

function centroid(poly) {
  let cx = 0, cy = 0;
  poly.forEach(([x, y]) => { cx += x; cy += y; });
  return [cx / poly.length, cy / poly.length];
}

function polyPoints(poly, size) {
  return poly.map(([x, y]) => `${(x * size).toFixed(2)},${(y * size).toFixed(2)}`).join(' ');
}

export default function NorthIndianChart({
  size = 340,
  lagnaSign = 0,
  houseSigns,
  planets = {},
  title = '',
  onClickPlanet,
  onClickHouse,
  onClickLagna,
  filteredPlanets,
  showDegrees = false,
  chartId = 'chart',
}) {
  const s = size;
  // Derive houseSigns if not provided: sign at house n = (lagnaSign + n - 1) % 12
  const signs = houseSigns && houseSigns.length === 12
    ? houseSigns
    : Array.from({ length: 12 }, (_, i) => (lagnaSign + i) % 12);

  // Build planet → house map: house = (planetSign - lagnaSign + 12) % 12 + 1
  const planetsByHouse = {};
  Object.entries(planets).forEach(([name, info]) => {
    if (filteredPlanets && !filteredPlanets.has(name)) return;
    if (info?.sign == null) return;
    const h = ((info.sign - lagnaSign) % 12 + 12) % 12 + 1;
    if (!planetsByHouse[h]) planetsByHouse[h] = [];
    planetsByHouse[h].push({ name, ...info });
  });

  return (
    <div className="inline-block" data-testid={`chart-${chartId}`}>
      {title && (
        <div className="text-center text-xs font-heading text-gold mb-1.5 tracking-wider">{title}</div>
      )}
      <svg viewBox={`0 0 ${s} ${s}`} width={s} height={s} className="block"
           style={{ background: 'linear-gradient(135deg, rgba(76,29,149,0.15), rgba(255,215,0,0.04))' }}>

        {/* Cell polygons — clickable hit-areas */}
        {Object.entries(HOUSE_POLY).map(([hn, poly]) => {
          const houseNum = Number(hn);
          const isLagna = houseNum === 1;
          return (
            <polygon
              key={`poly-${hn}`}
              data-testid={`chart-${chartId}-cell-${hn}`}
              points={polyPoints(poly, s)}
              fill="transparent"
              stroke="rgba(255,215,0,0.0)"
              style={{ cursor: 'pointer' }}
              onClick={() => {
                if (isLagna && onClickLagna) onClickLagna();
                else if (onClickHouse) onClickHouse(houseNum);
              }}
            />
          );
        })}

        {/* Outer square */}
        <rect x="1" y="1" width={s - 2} height={s - 2} fill="none" stroke="#FFD700" strokeWidth="1.5" />
        {/* Diagonals */}
        <line x1="0" y1="0" x2={s} y2={s} stroke="#FFD700" strokeWidth="1.2" opacity="0.7" />
        <line x1={s} y1="0" x2="0" y2={s} stroke="#FFD700" strokeWidth="1.2" opacity="0.7" />
        {/* Inner diamond */}
        <polygon points={`${s/2},0 ${s},${s/2} ${s/2},${s} 0,${s/2}`}
                 fill="none" stroke="#FFD700" strokeWidth="1.4" opacity="0.9" />

        {/* Per-cell content: sign number + planets */}
        {Object.entries(HOUSE_POLY).map(([hn, poly]) => {
          const houseNum = Number(hn);
          const [cx, cy] = centroid(poly).map(v => v * s);
          const signIdx = signs[houseNum - 1];
          const isLagna = houseNum === 1;
          const planetsHere = planetsByHouse[houseNum] || [];
          const isDiamondCell = [1, 4, 7, 10].includes(houseNum);

          // Sign-number anchor (small, traditional)
          const signFontSize = s * 0.035;
          const signOffsetY = isDiamondCell ? -s * 0.10 : -s * 0.05;

          return (
            <g key={`cell-${hn}`}>
              {/* Sign number (traditional NI: written as 1=Aries .. 12=Pisces) */}
              <text
                x={cx}
                y={cy + signOffsetY}
                fontSize={signFontSize}
                textAnchor="middle"
                fill="rgba(255,215,0,0.7)"
                style={{ pointerEvents: 'none', fontFamily: 'serif', fontWeight: 600 }}
                data-testid={`chart-${chartId}-sign-${hn}`}
              >
                {signIdx + 1}
              </text>

              {/* Lagna marker on house 1 */}
              {isLagna && (
                <text
                  x={cx}
                  y={cy + signOffsetY - s * 0.04}
                  fontSize={s * 0.030}
                  fontWeight="700"
                  textAnchor="middle"
                  fill="#a78bfa"
                  style={{ cursor: 'pointer', pointerEvents: 'all' }}
                  onClick={(e) => { e.stopPropagation(); onClickLagna && onClickLagna(); }}
                  data-testid={`chart-${chartId}-lagna-marker`}
                >
                  ★ La
                </text>
              )}

              {/* Planets — laid out HORIZONTALLY in compact rows so they stay within cell */}
              {planetsHere.length > 0 && (() => {
                // 1 planet  → 1 row × 1 col
                // 2-3       → 1 row × N cols
                // 4-5       → 2 rows
                // 6+        → 2 rows + smaller font
                const n = planetsHere.length;
                const rows = n <= 3 ? 1 : 2;
                const perRow = Math.ceil(n / rows);
                const fontSize = n >= 6 ? s * 0.034 : n >= 4 ? s * 0.038 : s * 0.042;
                // Spacing
                const colSpacing = isDiamondCell ? s * 0.075 : s * 0.060;
                const rowSpacing = fontSize * 1.1;
                const startY = cy + s * 0.005;

                return planetsHere.map((p, idx) => {
                  const row = Math.floor(idx / perRow);
                  const col = idx % perRow;
                  const totalInRow = (row === rows - 1)
                    ? (n - row * perRow)
                    : perRow;
                  const rowWidth = (totalInRow - 1) * colSpacing;
                  const x = cx - rowWidth / 2 + col * colSpacing;
                  const y = startY + (row - (rows - 1) / 2) * rowSpacing;
                  const glyph = PLANET_GLYPH[p.name] || p.name;
                  const marks = [];
                  if (p.retro) marks.push('*');
                  if (p.combust) marks.push('^');
                  if (p.exalted) marks.push('↑');
                  if (p.debilitated) marks.push('↓');
                  if (p.vargottama) marks.push('□');
                  return (
                    <g key={`p-${chartId}-${p.name}`}
                       data-testid={`chart-${chartId}-planet-${p.name}`}
                       data-house={houseNum}
                       data-sign={p.sign}
                       style={{ cursor: 'pointer' }}
                       onClick={(e) => { e.stopPropagation(); onClickPlanet && onClickPlanet(p.name); }}>
                      <text
                        x={x}
                        y={y}
                        fontSize={fontSize}
                        textAnchor="middle"
                        fill={PLANET_COLOR[p.name] || '#FFD700'}
                        fontWeight="700"
                        style={{ fontFamily: 'system-ui, sans-serif' }}
                      >
                        {glyph}{marks.join('')}
                        {showDegrees && p.deg !== undefined && (
                          <tspan fontSize={fontSize * 0.65} fill="rgba(255,255,255,0.75)" dx="2">
                            {Math.round(p.deg)}°
                          </tspan>
                        )}
                      </text>
                    </g>
                  );
                });
              })()}
            </g>
          );
        })}
      </svg>
    </div>
  );
}
